import axios from 'axios';

import { paths } from 'src/routes/paths';

import { HOST_API } from 'src/config-global';
import { list } from 'src/theme/core/components/list';
import { get } from 'lodash';

// ----------------------------------------------------------------------

// Net Scan API instance
const NET_SCAN_API = import.meta.env.VITE_NET_SCAN_API;

export const netScanAxiosInstance = axios.create({
  baseURL: `https://${NET_SCAN_API}`,
});

// Add request interceptor to include authorization token
netScanAxiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
netScanAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      console.error('Unauthorized access to Net Scan API');
    }
    return Promise.reject(error);
  }
);

// ----------------------------------------------------------------------

let isGeneratingToken;

const generateNewToken = async () => {
  try {
    const res = await axiosInstance.get(`auth/refresh`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('refreshToken')}`,
      },
    });

    return res.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

const handleError = async (error) => {
  let err = {
    status: 500,
    message: 'Something went wrong, please try again.',
  };
  if (error.response) {
    if (error.response.status === 401) {
      const origReq = error.config;
      try {
        origReq._retry = true;
        if (!isGeneratingToken) isGeneratingToken = generateNewToken();

        const { accessToken } = await isGeneratingToken;
        localStorage.setItem('accessToken', accessToken);
        origReq.headers.Authorization = `Bearer ${accessToken}`;
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        return axiosInstance(origReq);
      } catch (tokenErr) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        const searchParams = new URLSearchParams({
          returnTo: window.location.pathname,
        }).toString();

        const loginPath = paths.auth.jwt.login;

        const href = `${loginPath}?${searchParams}`;

        window.location = href;
      } finally {
        isGeneratingToken = undefined;
      }
    } else if (error.response.status === 403) {
      alert('You do not have permission to access this resource');
      // localStorage.removeItem('accessToken');
      // localStorage.removeItem('refreshToken');
      // localStorage.removeItem('user');
      // const searchParams = new URLSearchParams({
      //   returnTo: window.location.pathname,
      // }).toString();

      // const loginPath = paths.auth.login;

      // const href = `${loginPath}?${searchParams}`;

      // window.location = href;
    } else if (error.response.status === 500) {
      err = {
        status: error.response.status,
        message: 'Something went wrong, please try again.',
      };
    } else if (error.response.status === 400) {
      err = {
        message: error.response.data.message || err.message,
        status: error.response.status,
        errors: error.response.data.messages || {},
      };
    } else {
      err = {
        status: error.response.status,
        message: error.response.data.message || 'Something went wrong, please try again.',
      };
    }
  }
  return Promise.reject(err);
};

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use((res) => res, handleError);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/auth/me',
    login: '/auth/signIn',
    send_otp: '/auth/send-otp',
    register: '/api/auth/register',
    reset_password: '/auth/reset-password',
    changePassword: '/auth/change-password',
    verify_otp: '/auth/verify-otp',
    verify_token: '/auth/verify-token',
    sso_login: '/auth/sso-login',
    logout: `auth/logout`,
    change_password_only: (userId) => `/auth/change-password-only/${userId}`,
    keycloak_login: 'auth/keycloak-login',
    global_app_details: `/auth/sso/microsoft/global-app-details`,
    keycloak_login_microsoft: `/auth/keycloak/identity-provider/microsoft`,
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  users: {
    list: '/user',
    user_list: 'user/user-list',
    edit: (id) => `/user/${id}`,
    add_image: (id) => `/user/image/${id}`,
    profile_edit: 'user/edit-profile',
    active_users: '/user/active-users',
    list_users: 'user/listUsers/by-name/department',
    previous_assets: (id) => `user/previous-assets/${id}`,
    technician_list: '/user/technician/list',
    locations: '/api/v1/users/location',
    departments: '/api/v1/users/department',
    listCmmsTechnicians: '/api/v1/users/list/cmms-technicians',
  },
  designation: {
    list: '/user/designation',
    create: '/user/create-designation',
    delete: (id) => `/user/delete-designation/${id}`,
  },
  department: {
    list: '/user/department',
    create: '/user/create-department',
    delete: (id) => `/user/delete-department/${id}`,
  },
  roles: {
    list: '/user/roles',
  },
  location: {
    list: '/user/location',
    new_list: '/user/location-list/count-by-country',
    create: '/user/create-location',
    edit: (id) => `/user/delete-location/${id}`,
    get_country_list: 'asset/get-all-countries',
    get_states_by_country: (country) => `asset/list/states/${country}`,
    get_cities_by_state: (country, state) => `asset/list/city/${country}?stateName=${state}`,
    get_tabs_by_country: (country) =>
      `/user/location/rooms-by/state-and-country/wise/count?country=${country}`,
    get_data_by_country_and_state: (country, state) =>
      `/user/location/rooms-by/state-and-country/wise?country=${country}&state=${state}`,
    get_buildings_by_city: (country, state, city) =>
      `/user/building/loc-wise/search?country=${country}&state=${state}&city=${city}`,
    get_buildings_by_id: (locationId) => `/user/building/loc-wise/search?locationId=${locationId}`,
    get_floors_by_building_id: (buildingId) =>
      `/user/building-floor/buliding-wise/search?buildingId=${buildingId}`,
    get_rooms_by_floor_id: (floorId, buildingId) =>
      `user/building-room/loc-wise/floor-wise/search?floorId=${floorId}&buildingId=${buildingId}`,
  },
  vendor: {
    list: '/vendor',
    edit: (id) => `/vendor/${id}`,
    create_company: '/vendor/create/support-company',
    edit_company: (id) => `/vendor/edit/support-company/${id}`,
    company_list: '/vendor/list/support-company',
    add_company_to_asset: (assetId, companyId) =>
      `/vendor/add/support-company/${assetId}/${companyId}`,
    delete_company_from_asset: (assetId, companyId) =>
      `/vendor/delete/company-asset/${assetId}/${companyId}`,
    delete_company: (companyId) => `/vendor/delete/support-company/${companyId}`,
    delete_record: (recordId) => `/vendor/delete/record/${recordId}`,
    edit_record: (recordId) => `/vendor/edit/record/${recordId}`,
    country_list: '/asset/get-all-countries',
    add_record: '/vendor/create/record',
    create_support_type: '/vendor/create/support-type',
    get_support_type: '/vendor/list/support-type',
    get_record: (assetId) => `/asset/list/record/${assetId}`,
    list_company_by_assetId: (assetId) => `/vendor/list/company/${assetId}`,
  },
  expiry_alerts: {
    list: '/asset/list/expiry',
    count: '/asset/count/list/expiry',
    excel: 'asset/all/excel/alerts',
  },
  make: {
    list: '/asset/asset-make',
    create: '/asset/create-asset-make',
    edit: (id) => `/asset/asset-make/${id}`,
  },
  ticket_config: {
    create: '/ticket/configuration',
    getStages: '/ticket/configuration',
    edit: (id) => `/ticket/configuration/${id}`,
    assign: '/ticket/configuration/assignUser',
    get_assigned_data: '/ticket/configuration/open',
    delete_stage: (stageId) => `/ticket/configuration/delete/${stageId}`,
  },
  amc_providers: {
    list: '/asset/list/amc-providers-section',
  },
  asset_type: {
    list: '/asset/type?type=asset',
    list_by_login: 'asset/type/by-login',
    list_without_software: 'asset/type/not-include/software?type=asset',
    by_category: (categoryName) => `/asset/type/by-category?categoryName=${categoryName}`,
    create: '/asset/create-type',
    edit: (id) => `/asset/asset-type/${id}`,
    get_labels: (assetTypeId) => `/asset/find-all-asset/labels/${assetTypeId}`,
    assetType_not_included: `asset/list-type/not-include/created`,
  },
  asset_type_count: {
    list: '/asset/typeCounts?type=asset',
  },
  user_list: {
    list: '/user/user-list',
    user_by_role: '/user/list/by/role/showto',
    list_by_filter: '/user/listAllUsers/filter',
  },
  manager_list: {
    list: '/user/manager-list',
  },
  file_upload: {
    image: (fileType) => `/user/upload/${fileType}`,
  },
  dashboard: {
    assets: '/asset/user-assets',
  },
  asset: {
    list: '/asset/find-all-asset',
    available_list: '/asset/find-all-asset?assetStatus=available',
    create: '/asset/create-asset',
    edit: (id) => `/asset/update-asset/${id}`,
    get_asset_byId: (id) => `/asset/asset-by/${id}`,
    change_asset_status: (id) => `/asset/change-asset/status/${id}`,
    get_asset_history: (id) => `/asset/asset-history/${id}`,
    get_asset_by_userId: (id) => `/asset/asset-by/user/${id}`,
    asset_assign: (assetId) => `/asset/update-asset/assign/${assetId}`,
    count: '/asset/count-all-asset',
    new_assets: '/dashboard/newly-added/assets',
    asset_list_by_userId: (id) => `/asset/list-asset/${id}`,
    get_asset_categories: `/asset/list-categories`,
    create_category: '/asset/create-category',
    delete_category: (id) => `/asset/delete-category/${id}`,
    get_software_assigned_users: `/asset/list/assigned-users`,
    assign_software_asset: `/asset/add/assign/users`,
    unassign_software_asset: `/asset/unassign/users`,
    software_expiry: '/asset/list/software/expiry',
    software_alert_count: `/asset/count/list/expiry/software`,
    software_timeline: `/asset/timeline`,
    asset_configuration_count: '/asset/count/asset-category',
    enable_disable: (assetId) => `/asset/asset-enable/disable/${assetId}`,
    asset_depreciation_filters_dropdown: 'asset/find-filter-types',
    get_asset_depreciation_list: '/asset/list/depreciation-report',
    get_asset_type_by_category: '/asset/type/by-category',
    summaryReport: '/pdf/generate-asset/summary-report',
    amc_alerts: '/asset/list/expiry/amc',
    amc_contract_alerts: '/amc-contract/list-all/alerts',
    amc_contract_alerts_count: '/amc-contract/count/alerts',
    create_record: '/asset/create/record',
    edit_record: (id) => `/asset/edit/record/${id}`,
    asset_type_list: '/asset/find-all-asset/grouped-by-assetType',
    add_current_location: (assetId) => `/asset/add/current-location/${assetId}`,
    get_nearby_locations: '/asset/list/locations-nearBy',
    create_physical_location: '/user/create-physical-location',
    physical_location_list: '/user/physical-location/list',
  },
  asset_tag: {
    create: '/asset/create-tag',
    list: '/asset/tags?type=asset',
    tag_update: (tagId) => `/asset/tags/disable/${tagId}`,
    all_tags: '/asset/list/tags',
    tag_count: '/asset/count/list/tags',
  },
  checklist: {
    create: '/checklist/create/config',
    type: '/checklist/type/all',
    view: (id) => `/checklist/by/config/${id}`,
    edit: (id) => `/checklist/update/config/by/${id}`,
    search: (id) => `/checklist/by/config/search/${id}`,
    delete: (id) => `/checklist/delete/config/by/${id}`,
    list: '/checklist/all/config',
    update: '/checklist/create/update-checklist',
    update_without: '/checklist/create/update-checklist/without',
    get_data: (assetId) => `/checklist/by/asset/${assetId}`,
    get_data_by_id: (id) => `/checklist/by/${id}`,
    get_checklist_by_name: (id, name) => `/checklist/by/name/${id}?name=${name}`,
    get_list_by_assetId: (id) => `/checklist/listchecklist/asset/${id}`,
    getChecklistOutside: (id) => `/checklist/listchecklist/amc/asset/${id}`,
  },
  item: {
    create: '/asset/create-asset-item',
    list: '/asset/list/asset-item',
    tag: '/asset/create-itemTag',
    tag_list: '/asset/tags?type=item',
    get_item: (id) => `/asset/item/${id}`,
    update_item: (id) => `/asset/update-asset-item/${id}`,
    get_labels: (itemTypeId) => `/asset/find-all-item/labels/${itemTypeId}`,
  },
  // TICKET
  ticket: {
    create_asset_request_ticket: '/ticket/create/direct/newrequest-ticket',
    create_asset_issue_ticket: '/ticket/create/direct/issue-ticket',
    approval_list: '/ticket/all',
    approval_list_count: '/ticket/count/all',
    ticket_list: 'ticket/all/approved',
    create_label: '/ticket/create/label',
    get_labels: 'ticket/get/label',
    assignee_list: '/user/listAllUsers/ticket',
    technician_and_users_list: '/ticket/supported/user/list',
    change_ticket_status: (ticketId) => `/ticket/change-ticket/status/${ticketId}`,
    change_ticket_stage: (ticketId) => `/ticket/change-stage/${ticketId}`,
    edit: (ticketId) => `/ticket/update-ticket/${ticketId}`,
    view: (ticketId) => `/ticket/view-one-ticket/${ticketId}`,
    add_subtask: (ticketId) => `/ticket/add-subtask/ticket/${ticketId}`,
    create_subtask: '/ticket/create/subtask',
    get_subtask: (ticketId) => `/ticket/get/subtask/${ticketId}`,
    create_comment: '/ticket/create/comment',
    get_comment: (ticketId) => `/ticket/all/comment/${ticketId}`,
    create_resolution: '/ticket/create/resolution',
    get_resolution: '/ticket/get/resolution',
    add_resolution: (ticketId) => `/ticket/add-resolution/ticket/${ticketId}`,
    get_history: (id) => `/ticket/history/${id}`,
    get_returned_items: (ticketId) => `/ticket/returned/items/${ticketId}`,
    remove_returned_items: (ticketId, itemId) =>
      `/ticket/remove/returned/items/${ticketId}/${itemId}`,
    get_added_items: (ticketId) => `/ticket/added/items/${ticketId}`,
    add_items: (ticketId) => `/ticket/update-ticket/items/${ticketId}`,
    remove_item: (ticketId, itemId) => `/ticket/delete-ticket/items/${ticketId}/${itemId}`,
    kanban: '/ticket/all/kanban',
    stage_tabs: '/ticket/count/ticketStage/all',
    admin_stage_tabs: '/ticket/count/ticketStage/all/admin',
    change_mod_status: (ticketId) => `/ticket/change-modify/status/${ticketId}`,
    assign_asset: (ticketId, assetId) => `/ticket/add-asset/ticket/${ticketId}/${assetId}`,
    unAssign_asset: (ticketId, assetId) => `/ticket/remove-asset/ticket/${ticketId}/${assetId}`,
    technician_list: '/ticket/technician/list',
    technician_kanban: '/ticket/technician/list/kanban',
    technician_stage_tabs: '/ticket/count/ticketStage/technician/all',
    kanban_order_change: '/ticket/configuration/kanbanOrder',
    closed_tickets: '/ticket/list/closed',
    // ticket category in master settings-------------------------
    create_ticket_category_type: '/ticket/create-ticket/catgeory-type',
    get_ticket_category_type_list: '/ticket/list/catgeory-type',
    get_active_ticket_category_type_list: '/ticket/list/catgeory-type?isActive=true',
    change_ticket_category_status: (ticketCategoryId, status) =>
      `/ticket/deactivate/activate-ticket/catgeory-type/${ticketCategoryId}?isActive=${status}`,
    delete_ticket_category_type: (ticketCategoryId) =>
      `/ticket/delete-ticket/catgeory-type/${ticketCategoryId}`,
    get_ticket_category_type_list_count: '/ticket/count/catgeory-type',
    // ticket assignments in master settings-------------------------
    get_ticket_assignments_data: (stageName, typeId) =>
      `/ticket/get-one/ticket-assignment?stageName=${stageName}&typeId=${typeId}`,
    get_assign_to_options: '/ticket/list/user-by/department-category',
    create_ticket_assignment: '/ticket/create-update-ticket/assignment',
    previous_ticket_data: 'ticket/list-previous/by-asset',
    new_request_history: '/ticket/new-request/history',
    new_request_count: '/ticket/count/newRequest-against-user',
    my_assets: (ticketId) => `/dashboard/my-asset/tickets/by-ticketId/${ticketId}`,
    resolve_ticket: (ticketId) => `/ticket/resolve/ticket/${ticketId}`,
  },
  technician_reports: {
    ticket_wise_list: '/user/ticket-assigneduser-wise/report',
    technician_list: '/user/list/technican-assetAdmin',
    asset_category_wise_list: '/user/ticket/asset-category-wise/report',
    ticket_type_wise: '/user/ticket/type-wise/report',
  },
  userAssets: {
    createRequest: '/ticket/create/newrequest-ticket',
    create_general_ticket: '/ticket/create/general-ticket',
    get_general_ticket: '/ticket/general/ticket/list',
    general_ticket_count: '/ticket/count/general/ticket/list',
    get_general_ticket_by_id: (id) => `/ticket/view/general/ticket/${id}`,
    edit_general_ticket_by_id: (id) => `/ticket/edit/general/ticket/${id}`,
  },
  approvalList: {
    list: '/ticket/approval/list-asset',
    admin_list: '/ticket/approval/list-asset/admin',
    count: '/ticket/count/manager/all',
    admin_count: 'ticket/count/manager/all/admin',
    view: (id) => `/ticket/view-one-ticket/${id}`,
    updateStatus: (id) => `/ticket/change-ticket/approval/status/${id}`,
    ticketCreate: '/ticket/create/issue-ticket',
  },
  approvalManager: {
    allTicket: '/ticket/approval-manager/list-all',
    count: '/ticket/count/ticketStage/all',

    viewTicket: (id) => `/ticket/view-ticket/${id}`,
    ticketHistory: (ticketId) => `/ticket/history/user/${ticketId}`,
    ticketTypes: 'ticket/configuration',
    myTickets: 'ticket/list/my-tickets',
    viewAsset: (id) => `/asset/asset-by/${id}`,
  },
  adminDash: {
    ticketCounts: `ticket/count/ticketStage/admin/dashboard`,
    assetCounts: `asset/total/cost-counts`,
    assetExpriry: `asset/timeline`,
    hardwareAlerts: `asset/list/expiry?categoryName=Hardware`,
    softwareAlerts: `/asset/list/software/expiry`,
    otherAlerts: 'asset/list/expiry?categoryName=Others',
    assetCountByCate: `dashboard/asset-count-by-category`,
    assignUnAssignAssets: `asset/count/asset/assign-unassign/list`,
    assetByLocations: `dashboard/asset-count-by-locations`,
    getAllYearMonth: '/asset/get-all/years-months',
    topSpendAssets: 'asset/top/spend-data',
    assetSpend: 'asset/spend-data',
    costOwnership: 'asset/cost/ownership/spend-data',
    userSpendList: 'user/spend-list',
    user_reseller_vendor: 'user/count/user-reseller-vendor',
    asset_locations: 'dashboard/asset-count-by-locations',
    avg_spend_person: 'dashboard/spend-summary',
    category_counts: 'asset/count/asset/category-types/count',
    itSupport: 'ticket/itSupport-dasboard-chart',
    maintenanceAlert: '/asset/list/maintenance-alert/amc-dashboard',
    maintenanceCount: 'asset/count/list/maintenance-alert/amc-dashboard',
    amcAlerts: `/amc-contract/list-all/alerts`,
    amc_visit_alerts: '/amc-contract/list-amc-contract/dashboard',
    amc_visit_alerts_count: '/amc-contract/list-amc-contract/dashboard-counts',
  },
  technicianDash: {
    tickets_counts: 'ticket/count/ticketStage/technician/dashboard',
    ticket_statistics: 'ticket/techinian-overview-graph',
    asset_locations: 'dashboard/asset-count-by-locations',
    asset_types: 'dashboard/asset-count-by-types',
  },
  itSupportDash: {
    tickets_counts: 'ticket/itSupport-dasboard-chart',
    ticket_statistics: 'ticket/itSupport-overview-graph',
    asset_locations: 'dashboard/asset-count-by-locations',
    asset_types: 'dashboard/asset-count-by-types',
  },
  userDash: {
    tickets_counts: 'dashboard/user-count',
    asset_list: 'dashboard/my-asset',
    asset_list_count: 'dashboard/my-asset/category-count',
    my_tickets_list: 'dashboard/list-my/tickets',
    assetExpires: 'asset/list/expiry/user',
    softwareExpires: 'asset/list/software/expiry/user',
    confirmAsset: (id, assetId) => `/asset/confirm/asset?id=${id}&assetId=${assetId}`,
  },
  assetSpendOverview: {
    spendData: 'asset/get-all-cost/years-months',
    avg_spend_person: 'dashboard/spend-summary',
  },
  subscription: {
    details: '/add-companies/subscription-detail/company',
  },
  general: {
    globalSearch: '/global-search/global',
  },
  userRemoteSettings: {
    userEnableAccess: `/user/check/license-available`,
    remoteAssetsAvailable: `/asset/list-asset-remote/enabled`,
  },
  remoteDesktop: {
    list: '/remote/list',
    connect: (machineId) => `/remote/connect/${machineId}`,
    assetEnableAccess: `/asset/check/license-available`,
    assetDisableAccess: (assetId) => `/asset/disable-asset-remote?assetId=${assetId}`,
    remoteUrls: `/remote/urls`,
    connectAttended: `/remote/attended`,
  },
  amc_contracts: {
    create: '/amc-contract/create-contract',
    edit: (id) => `/amc-contract/edit-contract/${id}`,
    create_scope: '/amc-contract/create-scope',
    scope_list: '/amc-contract/list-scope',
    contract_list: '/amc-contract/list-all',
    vendor_list: '/vendor/list/amc-provider/vendor',
    reseller_list: '/vendor/list/amc-provider/reseller',
    get_contract: (id) => `/amc-contract/view-contract/${id}`,
    getContractFull: (id) => `/amc-contract/view-contract-full/${id}`,
    create_coverage: '/amc-contract/create-coverage',
    notActivatedAMCAssetList: (id) => `/amc-contract/list-amc-not-activated-asset/${id}`,
    linkAMCAssets: (id, assetIds) => `/asset/link/amc-contract/${id}?assetIds=${assetIds}`,
    linkedAMCAssetList: (id) => `/amc-contract/list-amc-assets-by/${id}`,
    linkedAMCAssetCount: `/asset/count-all-asset/by-category`,
    deleteLinkedAMCAsset: (id) => `/asset/remove/from-asset/${id}`,
    amc_history: (contractId) => `/amc-contract/list-amc-history-by/${contractId}`,
    amc_report_pdf: '/pdf/generate-amc/visit-report',
  },
  reports: {
    amc_contract_expiry_report: '/amc-contract/list-all/expiry-report',
    asset_subscription_report: `/asset/list/asset-by-subscription-type/report?subscriptionType=subscription`,
    vendor_report: '/vendor/list/report/vendor',
    vendor_report_excel: '/vendor/excel-report/vendor',
    reseller_report: '/vendor/list/support-company/report',
    reseller_report_excel: '/vendor/support-company/excel-report',
    technicianUserDetails: (id) => `/user/user-details/ticket-view/${id}`,
    technicianCategoryWiseList: `/user/ticket/asset-category-wise/report-assigned-user`,
    software_licence_report: '/asset/software-license/report',
    life_cycle_report: '/asset/life-cyle/report',
    fulfillment_report: 'ticket/asset-fullfillment/report',
    amc_maintenance_report: '/amc-contract/amc-maintenance/report/asset-wise',
    it_support_report: '/ticket/it-support/report',
    it_support_report_pdf: '/pdf/generate/It-support-report',
    it_support_report_count: '/ticket/it-support/report-count',
    support_agents: '/user/list/it-supports-agents',
    individual_technician_report:
      'user/ticket/asset-category-wise/report-assigned-user/excel-report',
  },
  userSettings: {
    onTaskSupportAccess: (id) => `/user/on-task/enable/${id}`,
  },
  discoverByImg: {
    list: '/discovery/list?type=Image',
    uploadImg: '/ai/upload-image',
    deleteAsset: (id) => `/discovery/list/delete/${id}`,
    clearAll: '/discovery/list-clear-all',
  },
  netDiscovery: {
    list: '/discovery/scanned-devices',
    scanDevices: `/discovery/fetch-scanned-devices`,
    deleteAsset: (id) => `/discovery/del/${id}`,
    downloadScanner: '/downloads/monitoring-agent',
  },
  periodicCheck: {
    create_periodic_check: '/periodic-checklist/create-periodic-checklist',
    periodic_check_list: '/periodic-checklist/list-periodic-checklist',
    periodic_detail_table: '/periodic-checklist/list-periodic-checklist/user-wise',
    periodic_detail_table_by_asset:
      '/periodic-checklist/list-periodic-checklist/asset-by/user-wise',
    periodic_detail_table_by_asset_for_admin:
      '/periodic-checklist/list-periodic-checklist/asset-by/user-wise/admin',
    mark_as_completed: '/periodic-checklist/to-mark-as-complete',
    mark_as_completed_by_admin: '/periodic-checklist/confirm-as-complete/by-admin',
    to_verify_user: (userId, periodicCheckId) =>
      `/periodic-checklist/to-verify/user?periodicCheckId=${periodicCheckId}&userId=${userId}`,
    update_periodic_checklist: '/periodic-checklist/update-periodic-checklist',
    abort: '/periodic-checklist/cancel',
  },
  chatbot: {
    assetsPerUser: `/asset/per-user`,
    getAllAssetTypes: `/asset/get-all-asset-types`,
    getSysDetails: `/asset/sys-details`,
    downloadAgent: (id, orgId) => `/asset/download-agent?assetId=${id}&orgId=${orgId}`,
  },
  audit: {
    get_users: '/user/list/by/roles',
    create: '/audit-sec/create/audit-report',
    add_internal_user: '/audit-sec/add-remove/internal-user/to-audit',
    add_external_user: '/audit-sec/add/external-user/to-audit',
    delete_external_user: (userId, auditId) =>
      `/audit-sec/remove/external-user/${userId}/${auditId}`,
    audit_list: '/audit-sec/view-all/audit-reports',
    audit_list_count: '/audit-sec/count-all/audit-reports',
    assign_asset: '/audit-sec/create-update/multiple-assets-in-audit',
    share_audit_link: '/audit-sec/send-mail/to-added-users',
    delete_audit: (auditId) => `/audit-sec/delete/audit-report/${auditId}`,
    view_audit: (auditId) => `/audit-sec/view-one/audit-report/${auditId}`,
    view_audit_for_temp_user: (auditId) => `/audit-sec/view-one/audit-report/temp-user/${auditId}`,
    get_assets_in_audit: (auditId) => `/audit-sec/view-all/assets-in-audit/${auditId}`,
    get_assets_in_audit_for_temp_user: (auditId) =>
      `/audit-sec/view-all/assets-in-audit/temp-user/${auditId}`,
    get_assigned_users: (auditId) => `/audit-sec/list/added-users/${auditId}`,
    get_assigned_users_for_temp_user: (auditId) =>
      `/audit-sec/list/added-users/temp-user/${auditId}`,
    delete_user: (userId) => `/audit-sec/remove/user/${userId}`,
    verify_asset: (auditId, assetId) =>
      `/audit-sec/update/audit-report/asset-added/${auditId}/${assetId}`,
    verify_asset_for_temp_user: (auditId, assetId) =>
      `/audit-sec/update/audit-report/asset-added/temp-user/${auditId}/${assetId}`,
    get_audit_summary: (auditId) => `/audit-sec/audit-summary/${auditId}`,
    get_audit_summary_for_temp_user: (auditId) => `/audit-sec/audit-summary/temp-user/${auditId}`,
    edit_audit: (auditId) => `audit-sec/update/audit-report/${auditId}`,
    edit_audit_for_temp_user: (auditId) => `audit-sec/update/audit-report/temp-user/${auditId}`,
    download_pdf: (auditId) => `/pdf/generate/audit-report/${auditId}`,
    download_pdf_for_temp_user: (auditId) => `/pdf/generate/audit-report/temp-user/${auditId}`,
    get_asset_categories_for_temp_user: `/asset/list-categories/temp-user`,
    asset_type_list_for_temp_user: `/asset/type/temp-user?type=asset`,
  },
  maintenanceRequests: {
    list: '/api/v1/maintenance-requests',
    details: (id) => `/api/v1/maintenance-requests/${id}`,
    stats: '/api/v1/maintenance-requests/stats',
    byAsset: (assetId) => `/api/v1/maintenance-requests/asset/${assetId}`,
    byAssignedUser: (userId) => `/api/v1/maintenance-requests/assigned/${userId}`,
    softDelete: (id) => `/api/v1/maintenance-requests/${id}/soft`,
    hardDelete: (id) => `/api/v1/maintenance-requests/${id}/hard`,
    restore: (id) => `/api/v1/maintenance-requests/${id}/restore`,
  },
  workOrder: {
    list: '/api/v1/work-orders',
    details: (id) => `/api/v1/work-orders/${id}`,
    stats: '/api/v1/work-orders/stats',
    calendar: '/api/v1/work-orders/calendar',
    reschedule: (id) => `/api/v1/work-orders/${id}/reschedule`,
    attachments: '/api/v1/work-orders/attachments',
    downloadAttachment: (id) => `/api/v1/work-orders/attachments/${id}/download`,
    deleteAttachment: (id) => `/api/v1/work-orders/attachments/${id}`,
    comments: '/api/v1/work-orders/comments',
    softDelete: (id) => `/api/v1/work-orders/${id}/soft`,
    hardDelete: (id) => `/api/v1/work-orders/${id}/hard`,
    restore: (id) => `/api/v1/work-orders/${id}/restore`,
    checklist: {
      create: '/api/v1/work-orders/create-checklist',
      list: '/api/v1/work-orders/list-checklist',
    },
  },
  preventiveMaintenance: {
    list: '/api/v1/preventive-maintenance',
    create: '/api/v1/preventive-maintenance',
    details: (id) => `/api/v1/preventive-maintenance/${id}`,
    update: (id) => `/api/v1/preventive-maintenance/${id}`,
    schedules: '/api/v1/preventive-maintenance/schedules',
    upcoming: '/api/v1/preventive-maintenance/upcoming',
    history: '/api/v1/preventive-maintenance/history',
    historyRecords: '/api/v1/preventive-maintenance/history/records',
    historyDetails: (id) => `/api/v1/preventive-maintenance/history/records/${id}`,
    calendar: '/api/v1/preventive-maintenance/calendar',
    tabCounts: '/api/v1/preventive-maintenance/tab-counts',
    stats: '/api/v1/preventive-maintenance/stats',
    start: (id) => `/api/v1/preventive-maintenance/${id}/start`,
    complete: (id) => `/api/v1/preventive-maintenance/${id}/complete`,
    softDelete: (id) => `/api/v1/preventive-maintenance/${id}/soft`,
    hardDelete: (id) => `/api/v1/preventive-maintenance/${id}/hard`,
    restore: (id) => `/api/v1/preventive-maintenance/${id}/restore`,
  },
  inventory: {
    list: '/api/v1/inventory',
    details: (id) => `/api/v1/inventory/${id}`,
    stats: '/api/v1/inventory/stats',
    statistics: '/api/v1/inventory/statistics',
    lowStock: '/api/v1/inventory/low-stock',
    outOfStock: '/api/v1/inventory/out-of-stock',
    softDelete: (id) => `/api/v1/inventory/${id}/soft`,
    hardDelete: (id) => `/api/v1/inventory/${id}/hard`,
    restore: (id) => `/api/v1/inventory/${id}/restore`,
    usage_history: (itemId) => `/api/v1/inventory/${itemId}/history`,
    usage_history_count: (itemId) => `/api/v1/inventory/history-sec/count/${itemId}`,
    count: '/api/v1/inventory/count/all',
  },
  cmms_assets: {
    list: '/api/v1/assets',
    bulk: `/api/v1/assets/bulk`,
    listCategories: '/api/v1/assets/list-categories?assetClassification=cmms',
    typesByCategory: '/api/v1/assets/type/by-category',
    details: (id) => `/api/v1/assets/${id}`,
    updateQr: (id) => `/api/v1/assets/${id}/qr`,
    associateChild: `/api/v1/assets/associate/child`,
    disassociateChild: `/api/v1/assets/disassociate/child`,
    softDelete: (id) => `/api/v1/assets/${id}/soft`,
    grid_view: '/api/v1/assets/list-grid-view',
    count: '/api/v1/assets/grid-view/count/all',
  },
  cmms_dashboard: {
    assetCount: '/api/v1/dashboard/asset-count',
    workorderCount: '/api/v1/dashboard/workorder-count',
    topAssets: '/api/v1/dashboard/top-assets',
    cityDistribution: '/api/v1/dashboard/city-distribution',
    workorderByStatus: '/api/v1/dashboard/workorder-by-status',
    pmCompletionStats: '/api/v1/dashboard/pm-completion-stats',
    topUsedInventory: '/api/v1/dashboard/top-used-inventory',
    lowStockInventory: '/api/v1/dashboard/low-stock-inventory',
    technicianPerformance: '/api/v1/dashboard/technician-performance',
    assetByLocation: '/api/v1/dashboard/asset-by-location',
  },
  currency: {
    list: '/asset/get-all-countries-currency',
  },
  net_scan: {
    status: (macId) => `/devices/${macId}/status`,
    toggle_snmp: (macId) => `/devices/${macId}/toggle-snmp`,
    toggle_system_agent: (recordId) => `/devices/${recordId}/toggle-system-agent`,
  },
};

// ----------------------------------------------------------------------

// Net Scan API helpers
export const netScanApi = {
  getStatus: async (macId) => {
    try {
      const response = await netScanAxiosInstance.get(`/devices/${macId}/status`);
      const { data } = response;

      // Map API response to our expected format
      // API returns: { "_id": "688b2b2de2189cb810bd0a34", "status": "Offline", "snmp": false, "monitoring": false, "agent": false, "agentRecord": false, "snmpRecord": false }
      return {
        id: data._id,
        networkScan: data.monitoring, // Use monitoring field for network scan
        snmp: data.snmp,
        systemAgent: data.agent, // API uses "agent" field for system agent
        agentRecord: data.agentRecord, // Agent record availability
        snmpRecord: data.snmpRecord, // SNMP record availability
        rawStatus: data.status, // Keep original status for reference
      };
    } catch (error) {
      console.error('Failed to fetch net scan status:', error);
      throw error;
    }
  },

  toggleSnmp: async (recordId) => {
    try {
      const response = await netScanAxiosInstance.patch(`/devices/${recordId}/toggle-snmp`);
      return response.data;
    } catch (error) {
      console.error('Failed to toggle SNMP:', error);
      throw error;
    }
  },

  toggleSystemAgent: async (recordId) => {
    try {
      const response = await netScanAxiosInstance.patch(`/devices/${recordId}/toggle-system-agent`);
      return response.data;
    } catch (error) {
      console.error('Failed to toggle System Agent:', error);
      throw error;
    }
  },

  toggleNetworkScan: async (recordId) => {
    try {
      const response = await netScanAxiosInstance.patch(`/devices/${recordId}/toggle-monitoring`);
      return response.data;
    } catch (error) {
      console.error('Failed to toggle Network Scan:', error);
      throw error;
    }
  },
};
